import { useTranslation } from 'react-i18next';

import { ErrorState, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { ApiError } from '@nucleus-commerce/api-client/transport';
import { WEIGHT_UNITS, type WeightUnitCode } from '@nucleus-commerce/core';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@nucleus-commerce/ui/components/card';
import { Label } from '@nucleus-commerce/ui/components/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';

import { SECTION_ANCHORS } from '@/shared/navigation/section-anchors';
import { session } from '@/shared/session/session';

import { useOrganizationSettings } from '../hooks/use-organization-settings';
import { useUpdateOrganizationSettings } from '../hooks/use-update-organization-settings';

/**
 * The unit the product and variant weight inputs start on. It never converts a stored weight:
 * each one keeps the unit it was entered in. Owners only, like every organisation setting.
 */
export function DefaultWeightUnitCard() {
  const { t } = useTranslation(['organization', 'common']);
  const message = useErrorMessage();
  const { me } = session.useSession();
  const query = useOrganizationSettings();
  const update = useUpdateOrganizationSettings();
  const isOwner = me?.role.key === 'owner';
  const unit = query.data?.catalog.defaultWeightUnit ?? 'g';

  return (
    <Card
      id={SECTION_ANCHORS.weightUnit}
      className="max-w-2xl"
      data-tour="org.settings.weight-unit"
    >
      <CardHeader>
        <CardTitle>{t('catalog.title')}</CardTitle>
        <CardDescription>{t('catalog.description')}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {query.isError ? (
          <ErrorState error={query.error} onRetry={() => void query.refetch()} compact />
        ) : query.isPending ? (
          <Skeleton className="h-10" />
        ) : (
          <div className="grid gap-2">
            <Label htmlFor="default-weight-unit">{t('catalog.defaultWeightUnit')}</Label>
            <Select
              value={unit}
              disabled={!isOwner || update.isPending}
              onValueChange={(next) => {
                update.reset();
                update.mutate({ catalog: { defaultWeightUnit: next as WeightUnitCode } });
              }}
            >
              <SelectTrigger id="default-weight-unit" className="w-56">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {WEIGHT_UNITS.map((code) => (
                  <SelectItem key={code} value={code}>
                    {t(`common:weightUnits.${code}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-muted-foreground text-sm">{t('catalog.defaultWeightUnitHint')}</p>
          </div>
        )}
        {!isOwner ? (
          <p className="text-muted-foreground text-sm">{t('security.ownersOnly')}</p>
        ) : null}
        {update.isError ? (
          <Alert variant="destructive" role="alert">
            <AlertDescription>
              {update.error instanceof ApiError && update.error.kind === 'forbidden'
                ? t('catalog.notOwner')
                : message(update.error)}
            </AlertDescription>
          </Alert>
        ) : null}
      </CardContent>
    </Card>
  );
}
